import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  XCircleIcon,
  CheckCircleIcon,
  LockClosedIcon,
  MapPinIcon,
  IdentificationIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { formatPhoneNumber } from '@/utils/phoneUtils'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { 
  getUsers, 
  createUser, 
  deleteUser, 
  type User as ApiUser,
  type CreateUserRequest 
} from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import { getVehicles, type Vehicle } from '@/api/fleetApi'
import toast from 'react-hot-toast'

// Form schema for adding new driver
const addDriverSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().min(1, 'Phone number is required').regex(/^[+]?[0-9\s\-\(\)]+$/, 'Please enter a valid phone number'),
  countryCode: z.string().min(2, 'Country code is required').max(2, 'Country code must be 2 letters'),
  roleId: z.string().min(1, 'Role is required'),
  licenseNumber: z.string().optional(),
  licenseExpiryDate: z.string().optional(),
  emergencyContact: z.string().optional(),
  emergencyPhone: z.string().optional(),
  vehicleId: z.string().optional(),
})

type AddDriverData = z.infer<typeof addDriverSchema>

interface Driver extends ApiUser {
  licenseNumber?: string
  licenseExpiryDate?: string
  vehicleAssigned?: string
  emergencyContact?: string
  routesAssigned?: number
  totalStudents?: number
}

interface DriverStats {
  totalDrivers: number
  activeDrivers: number
  inactiveDrivers: number
  driversOnRoute: number
  availableDrivers: number
  byStatus: Record<string, number>
}

const DriversPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  
  // Available roles for driver creation
  const [driverRoleId, setDriverRoleId] = useState<string>('')
  const [loadingRoles, setLoadingRoles] = useState(false)

  // Available vehicles for assignment
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  
  // Stats state
  const [stats, setStats] = useState<DriverStats>({ 
    totalDrivers: 0, 
    activeDrivers: 0,
    inactiveDrivers: 0,
    driversOnRoute: 0,
    availableDrivers: 0,
    byStatus: {}
  })

  const addDriverForm = useForm<AddDriverData>({
    resolver: zodResolver(addDriverSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE', // Default to Kenya
      roleId: '',
    }
  })

  useEffect(() => {
    fetchDrivers()
    fetchDriverRole()
    fetchVehicles()
  }, [])

  useEffect(() => {
    // Re-fetch drivers when search or filter changes
    const timeoutId = setTimeout(() => {
      fetchDrivers()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter])

  const fetchDrivers = async () => {
    setLoading(true)
    try {
      // First get the driver role ID if we don't have it yet
      let roleId = driverRoleId
      if (!roleId) {
        const rolesResponse = await getRoles()
        const driverRole = rolesResponse.data?.find(role =>
          role.name.toLowerCase().includes('driver')
        )
        if (driverRole) {
          roleId = driverRole.id
          setDriverRoleId(roleId)
        }
      }

      const params: any = {
        search: searchQuery.trim() || undefined,
        limit: 100
      }

      // Use roleId for server-side filtering if available
      if (roleId) {
        params.roleId = roleId
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      const response = await getUsers(params)

      // Filter for drivers if we couldn't filter by roleId
      let driverUsers = response.users || []
      if (!roleId) {
        driverUsers = driverUsers.filter((user: ApiUser) =>
          user.role && user.role.toLowerCase().includes('driver')
        )
      }

      // Map to Driver interface with additional fields
      const driversWithExtras: Driver[] = driverUsers.map((driver: any) => ({
        ...driver,
        licenseNumber: driver.licenseNumber || driver.metadata?.licenseNumber || 'Not provided',
        licenseExpiryDate: driver.licenseExpiryDate || driver.metadata?.licenseExpiryDate || 'Not provided',
        vehicleAssigned: driver.vehicleAssigned || driver.metadata?.vehicleAssigned || 'None',
        emergencyContact: driver.emergencyContact || driver.metadata?.emergencyContact || 'Not provided',
        routesAssigned: driver.routesAssigned || 0,
        totalStudents: driver.totalStudents || 0
      }))

      setDrivers(driversWithExtras)

      // Calculate stats
      const activeCount = driversWithExtras.filter(d => d.status === 'Active').length
      const inactiveCount = driversWithExtras.filter(d => d.status === 'Inactive').length
      const onRouteCount = driversWithExtras.filter(d => d.routesAssigned && d.routesAssigned > 0).length

      setStats({
        totalDrivers: driversWithExtras.length,
        activeDrivers: activeCount,
        inactiveDrivers: inactiveCount,
        driversOnRoute: onRouteCount,
        availableDrivers: activeCount - onRouteCount,
        byStatus: {
          'Active': activeCount,
          'Inactive': inactiveCount,
          'On Route': onRouteCount,
          'Available': activeCount - onRouteCount
        }
      })

    } catch (error: any) {
      console.error('Failed to fetch drivers:', error)
      toast.error(error.response?.data?.message || 'Failed to load drivers')
      setDrivers([])
      setStats({
        totalDrivers: 0,
        activeDrivers: 0,
        inactiveDrivers: 0,
        driversOnRoute: 0,
        availableDrivers: 0,
        byStatus: {}
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchDriverRole = async () => {
    setLoadingRoles(true)
    try {
      const rolesResponse = await getRoles()
      const driverRole = rolesResponse.data.find(role => 
        role.name.toLowerCase().includes('driver')
      )
      
      if (driverRole) {
        setDriverRoleId(driverRole.id)
        addDriverForm.setValue('roleId', driverRole.id)
      } else {
        toast.error('Driver role not found in system')
      }
    } catch (error: any) {
      console.error('Failed to fetch driver role:', error)
      toast.error('Failed to load driver role')
    } finally {
      setLoadingRoles(false)
    }
  }

  const fetchVehicles = async () => {
    try {
      const response = await getVehicles({ status: 'active', limit: 100 })
      setVehicles(response.data.vehicles || [])
    } catch (error) {
      console.error('Failed to fetch vehicles:', error)
    }
  }

  const handleAddDriver = async (driverData: AddDriverData) => {
    try {
      console.log('Creating driver:', driverData)

      const createData: CreateUserRequest = {
        firstName: driverData.firstName,
        lastName: driverData.lastName,
        email: driverData.email,
        phone: driverData.phone,
        countryCode: driverData.countryCode,
        roleId: driverData.roleId,
        // Driver-specific fields
        licenseNumber: driverData.licenseNumber,
        licenseExpiryDate: driverData.licenseExpiryDate,
        emergencyContact: driverData.emergencyContact,
        emergencyPhone: driverData.emergencyPhone,
        vehicleId: driverData.vehicleId,
        // password will be auto-generated by backend
      }

      const response = await createUser(createData)
      console.log('Driver created:', response)

      toast.success(`Driver "${response.data.user.firstName} ${response.data.user.lastName}" created successfully!`)
      setShowAddModal(false)
      addDriverForm.reset()
      addDriverForm.setValue('roleId', driverRoleId) // Keep the role selected
      addDriverForm.setValue('countryCode', 'KE') // Reset to default country
      fetchDrivers()

    } catch (error: any) {
      console.error('Failed to create driver:', error)
      toast.error(error.response?.data?.message || 'Failed to create driver')
    }
  }

  const handleViewDriver = (driver: Driver) => {
    setSelectedDriver(driver)
  }

  const handleEditDriver = (driver: Driver) => {
    toast.success(`Edit "${driver.fullName}" - Coming soon!`)
  }

  const handleDeleteDriver = async (driver: Driver) => {
    if (confirm(`Are you sure you want to delete driver "${driver.fullName}"?`)) {
      try {
        await deleteUser(driver.id)
        toast.success(`Driver "${driver.fullName}" deleted successfully`)
        fetchDrivers()
      } catch (error: any) {
        console.error('Failed to delete driver:', error)
        toast.error(error.response?.data?.message || 'Failed to delete driver')
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800'
      case 'Inactive':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRouteStatusColor = (routesCount: number) => {
    if (routesCount === 0) return 'bg-gray-100 text-gray-800'
    if (routesCount >= 3) return 'bg-red-100 text-red-800'
    if (routesCount >= 2) return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-600 mr-3" />
            Drivers Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage school transport drivers, routes, and vehicle assignments
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Driver
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Drivers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalDrivers}</p>
            </div>
            <TruckIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeDrivers}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">On Route</p>
              <p className="text-2xl font-bold text-orange-600">{stats.driversOnRoute}</p>
            </div>
            <MapPinIcon className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available</p>
              <p className="text-2xl font-bold text-blue-600">{stats.availableDrivers}</p>
            </div>
            <UserIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-red-600">{stats.inactiveDrivers}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search drivers by name, email, or license..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Drivers</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Drivers Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drivers.map((driver) => (
            <motion.div
              key={driver.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewDriver(driver)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <TruckIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{driver.fullName}</h3>
                    <p className="text-sm text-gray-600">{driver.email}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(driver.status)}`}>
                  {driver.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Phone:</span>
                  <span className="text-gray-900 font-medium">{formatPhoneNumber(driver.phone, driver.countryCode)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">License:</span>
                  <span className="text-gray-900 font-medium">{driver.licenseNumber}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Vehicle:</span>
                  <span className="text-gray-900 font-medium">{driver.vehicleAssigned}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Routes:</span>
                  <span className={`px-1 py-0.5 text-xs rounded ${getRouteStatusColor(driver.routesAssigned || 0)}`}>
                    {driver.routesAssigned || 0} assigned
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  {driver.totalStudents || 0} students
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditDriver(driver)
                    }}
                    className="btn btn-ghost text-xs"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteDriver(driver)
                    }}
                    className="btn btn-ghost text-xs text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Drivers Table View */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    License
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Routes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {drivers.map((driver) => (
                  <motion.tr
                    key={driver.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewDriver(driver)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                          <TruckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{driver.fullName}</div>
                          <div className="text-sm text-gray-500">Driver ID: {driver.id.slice(-8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{driver.email}</div>
                      <div className="text-sm text-gray-500">{formatPhoneNumber(driver.phone, driver.countryCode)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{driver.licenseNumber}</div>
                      <div className="text-sm text-gray-500">Exp: {driver.licenseExpiryDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{driver.vehicleAssigned}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRouteStatusColor(driver.routesAssigned || 0)}`}>
                        {driver.routesAssigned || 0} routes
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(driver.status)}`}>
                        {driver.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditDriver(driver)
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteDriver(driver)
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {drivers.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No drivers found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first driver'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add First Driver
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Driver Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add New Driver</h3>
                    <p className="text-sm text-gray-500">Create a new driver account for school transport</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addDriverForm.handleSubmit(handleAddDriver)} className="p-6 space-y-6">
                
                {/* Personal Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addDriverForm.register('firstName')}
                      label="First Name"
                      placeholder="John"
                      error={addDriverForm.formState.errors.firstName?.message}
                    />

                    <Input
                      {...addDriverForm.register('lastName')}
                      label="Last Name"
                      placeholder="Smith"
                      error={addDriverForm.formState.errors.lastName?.message}
                    />

                    <Input
                      {...addDriverForm.register('email')}
                      type="email"
                      label="Email Address"
                      placeholder="john.smith@school.com"
                      error={addDriverForm.formState.errors.email?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <PhoneNumberInput
                        value={addDriverForm.watch('phone')}
                        countryCode={addDriverForm.watch('countryCode')}
                        onChange={(phone: string, countryCode: string) => {
                          addDriverForm.setValue('phone', phone)
                          addDriverForm.setValue('countryCode', countryCode)
                          addDriverForm.trigger(['phone', 'countryCode'])
                        }}
                        placeholder="Enter phone number"
                        error={addDriverForm.formState.errors.phone?.message || addDriverForm.formState.errors.countryCode?.message}
                        required
                      />
                    </div>
                  </div>
                </div>


                {/* License Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <IdentificationIcon className="h-5 w-5 text-green-600 mr-2" />
                    License Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addDriverForm.register('licenseNumber')}
                      label="License Number"
                      placeholder="e.g., DL123456789"
                      error={addDriverForm.formState.errors.licenseNumber?.message}
                    />

                    <Input
                      {...addDriverForm.register('licenseExpiryDate')}
                      type="date"
                      label="License Expiry Date"
                      error={addDriverForm.formState.errors.licenseExpiryDate?.message}
                    />
                  </div>
                </div>

                {/* Vehicle Assignment */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <TruckIcon className="h-5 w-5 text-orange-600 mr-2" />
                    Vehicle Assignment (Optional)
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Vehicle
                      </label>
                      <select
                        {...addDriverForm.register('vehicleId')}
                        className="form-input"
                      >
                        <option value="">Select a vehicle (optional)</option>
                        {vehicles.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.vehicleNumber} - {vehicle.make} {vehicle.vehicleModel} ({vehicle.capacity} seats)
                          </option>
                        ))}
                      </select>
                      {vehicles.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">No vehicles available. Add vehicles first.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <PhoneIcon className="h-5 w-5 text-red-600 mr-2" />
                    Emergency Contact (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addDriverForm.register('emergencyContact')}
                      label="Emergency Contact Name"
                      placeholder="e.g., Jane Smith"
                      error={addDriverForm.formState.errors.emergencyContact?.message}
                    />

                    <Input
                      {...addDriverForm.register('emergencyPhone')}
                      label="Emergency Contact Phone"
                      placeholder="e.g., +254712345678"
                      error={addDriverForm.formState.errors.emergencyPhone?.message}
                    />
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <LockClosedIcon className="h-5 w-5 text-purple-600 mr-2" />
                    Account Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Password is auto-generated by backend */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <LockClosedIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Secure Password</p>
                          <p className="text-xs text-blue-600">Auto-generated and sent via email</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <input
                        type="text"
                        value="Driver"
                        disabled
                        className="form-input bg-gray-50 text-gray-500"
                      />
                      <input
                        {...addDriverForm.register('roleId')}
                        type="hidden"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Driver Account Creation</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Driver will receive login credentials via email</p>
                        <p>• They can access route management and student tracking</p>
                        <p>• Vehicle can be assigned now or later</p>
                        <p>• Account will be created with Driver role permissions</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={loadingRoles || !driverRoleId}
                  >
                    <TruckIcon className="h-4 w-4 mr-2" />
                    {loadingRoles ? 'Loading...' : 'Add Driver'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Driver Details Modal */}
      <AnimatePresence>
        {selectedDriver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedDriver(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <TruckIcon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedDriver.fullName}</h3>
                      <p className="text-sm text-gray-500">School Transport Driver</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedDriver.status)}`}>
                    {selectedDriver.status}
                  </span>
                  <button
                    onClick={() => setSelectedDriver(null)}
                    className="text-gray-400 hover:text-gray-600 ml-4"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Email</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedDriver.email}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900 font-medium">{formatPhoneNumber(selectedDriver.phone, selectedDriver.countryCode)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Emergency Contact</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedDriver.emergencyContact}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedDriver.status)}`}>
                          {selectedDriver.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* License & Vehicle Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">License & Vehicle</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">License Number</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedDriver.licenseNumber}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">License Expiry</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedDriver.licenseExpiryDate}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Assigned Vehicle</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedDriver.vehicleAssigned}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Routes Assigned</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRouteStatusColor(selectedDriver.routesAssigned || 0)}`}>
                          {selectedDriver.routesAssigned || 0} routes
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Activity Summary</h4>
                  <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedDriver.totalStudents || 0}</div>
                      <div className="text-sm text-gray-600">Students Served</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{selectedDriver.routesAssigned || 0}</div>
                      <div className="text-sm text-gray-600">Active Routes</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">98%</div>
                      <div className="text-sm text-gray-600">On-Time Rate</div>
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Role: </span>
                      <span className="text-gray-900 font-medium">{selectedDriver.role}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Institution: </span>
                      <span className="text-gray-900 font-medium">{selectedDriver.institution}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Last Login: </span>
                      <span className="text-gray-900 font-medium">
                        {selectedDriver.lastLogin 
                          ? new Date(selectedDriver.lastLogin).toLocaleDateString() 
                          : 'Never'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Created: </span>
                      <span className="text-gray-900 font-medium">
                        {new Date(selectedDriver.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => handleEditDriver(selectedDriver)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Driver
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default DriversPage